// @ts-check
// Lista dos bots (/service/bots; contrato de bots, seção 22.1): cabeçalho com "Bots prontos" e "Criar bot"; busca e
// filtros na URL (situação, finalidade e número); tabela com Nome, Finalidade, Gatilhos, Situação, Iniciadas (30 dias),
// Transferidas (%), Concluídas sem pessoa (%) e Última alteração, colunas ordenáveis e o menu de cada linha (Editar,
// Estatísticas, Duplicar, Ativar ou Desativar, Excluir); no celular, cartões. Lista vazia convida a começar por um pronto.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { createBot, deactivateBot, deleteBot, duplicateBot, listBots } from '../../data/bots.js';
import { listChannels } from '../../data/conversations.js';
import { Button } from '../../ui/button.js';
import { Checkbox, EmptyState, Skeleton } from '../../ui/feedback.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { useDebouncedValue, useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatNumber } from '../../lib/format.js';
import {
  BOT_LIST_PURPOSES, BOT_LIST_STATUSES, BOT_TEMPLATES_PATH, activeBotFilterCount, botEditorHref, botListQueryParams, botStatsHref, botTriggerKinds,
  defaultBotOrderOf, filterAndSortBots, readBotListQuery,
} from '../../lib/bot-list.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowMenuButton } from '../flows/flow-menu.js';
import { lastChangeText } from '../flows/flows-page.js';
import { BotLifecycleConfirm, BotNameDialog } from './bot-dialogs.js';
import { BotPurposeChip, BotStatusChip } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/bot-list.js').BotSummary} BotSummary
 * @typedef {import('../../lib/bot-list.js').BotListQuery} BotListQuery
 * @typedef {import('../../lib/bot-list.js').BotSort} BotSort
 * @typedef {{ kind: 'create' } | { kind: 'deactivate' | 'delete', bot: BotSummary } | null} ListDialog
 */

const SKELETON_ROWS = 5;

/** Porcentagem com uma casa, ou "-" sem execuções. @param {number | null | undefined} value */
export const botRateText = (value) => (value === null || value === undefined ? '-' : `${formatNumber(Math.round(value * 1000) / 10, { decimals: 1 })}%`);

export function BotsPage() {
  const isMobile = useIsMobile();
  const query = readBotListQuery(searchParamsState.value);
  const [bots, setBots] = useState(/** @type {BotSummary[] | null} */ (null));
  const [channels, setChannels] = useState(/** @type {Array<{ id: string, label: string }>} */ ([]));
  const [loadError, setLoadError] = useState(/** @type {unknown} */ (null));
  const [dialog, setDialog] = useState(/** @type {ListDialog} */ (null));
  const [isBusy, setIsBusy] = useState(false);
  const [search, setSearch] = useState(query.q);
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const load = () => {
    setLoadError(null);
    listBots().then(setBots).catch((error) => {
      setLoadError(error);
      setBots(null);
    });
  };
  useEffect(() => {
    document.title = t('bots.list.title');
    load();
    listChannels().then((result) => setChannels(result.channels.filter((channel) => channel.type === 'WHATSAPP')
      .map((channel) => ({ id: channel.id, label: String(channel.settings?.displayPhoneNumber || channel.displayName || channel.handle) })))).catch(() => setChannels([]));
  }, []);
  useEffect(() => {
    if (debouncedSearch !== query.q) updateSearchParams({ q: debouncedSearch || null });
  }, [debouncedSearch]);

  /** @param {Partial<BotListQuery>} changes */
  const setQuery = (changes) => {
    updateSearchParams(botListQueryParams({ ...query, ...changes }));
    if (changes.q !== undefined) setSearch(changes.q);
  };
  const visible = useMemo(() => (bots ? filterAndSortBots(bots, query) : []), [bots, searchParamsState.value]);
  const hasFilters = query.q !== '' || activeBotFilterCount(query) > 0;

  /** @param {BotSummary} bot */
  const duplicate = async (bot) => {
    try {
      const copy = await duplicateBot(bot.id);
      navigate(botEditorHref(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };
  /** @param {BotSummary} bot */
  const rowMenuItems = (bot) => [
    { key: 'edit', text: t('bots.list.menu.edit'), icon: 'IconPencil', onSelect: () => navigate(botEditorHref(bot.id)) },
    { key: 'stats', text: t('bots.list.menu.stats'), icon: 'IconChartBar', onSelect: () => navigate(botStatsHref(bot.id)) },
    { key: 'test', text: t('bots.list.menu.test'), icon: 'IconMessageCircle', onSelect: () => navigate(botEditorHref(bot.id, { test: true })) },
    { key: 'duplicate', text: t('bots.list.menu.duplicate'), icon: 'IconCopy', onSelect: () => duplicate(bot) },
    bot.status === 'ACTIVE'
      ? { key: 'deactivate', text: t('bots.list.menu.deactivate'), icon: 'IconPlayerPause', onSelect: () => setDialog({ kind: 'deactivate', bot }) }
      : { key: 'activate', text: t('bots.list.menu.activate'), icon: 'IconPlayerPlay', onSelect: () => navigate(botEditorHref(bot.id)) },
    { key: 'delete', text: t('bots.list.menu.delete'), icon: 'IconTrash', accent: /** @type {const} */ ('danger'), separatorBefore: true, onSelect: () => setDialog({ kind: 'delete', bot }) },
  ];
  const closeDialog = () => setDialog(null);
  const hasBots = Boolean(bots && bots.length > 0);
  const headerActions = html`
    ${hasBots && !isMobile ? html`<${Button} title=${t('bots.list.templates')} icon="IconLayoutGrid" variant="secondary" size="small" onClick=${() => navigate(BOT_TEMPLATES_PATH)} />` : null}
    ${hasBots && isMobile ? html`<${FlowMenuButton} ariaLabel=${t('bots.list.moreActions')} icon="IconDotsVertical"
      items=${[{ key: 'templates', text: t('bots.list.templates'), icon: 'IconLayoutGrid', onSelect: () => navigate(BOT_TEMPLATES_PATH) }]} />` : null}
    <${Button} title=${t('bots.list.create')} icon="IconPlus" accent="blue" size="small" onClick=${() => setDialog({ kind: 'create' })} data-testid="bot-create-button" />`;

  /** @type {any} */
  let body;
  if (loadError) {
    body = html`<div class="flows-state"><${EmptyState} icon="IconAlertTriangle" title=${t('bots.list.loadError')}
      action=${html`<${Button} title=${t('bots.common.retry')} variant="secondary" size="small" onClick=${load} />`} /></div>`;
  } else if (bots === null) {
    body = html`<div class="flows-table-wrapper" aria-busy="true" data-testid="bots-loading">
      ${Array.from({ length: SKELETON_ROWS }, (_, index) => html`<div key=${index} class="flows-skeleton-row"><${Skeleton} width="36%" /><${Skeleton} width="12%" /><${Skeleton} width="16%" /><${Skeleton} width="12%" /></div>`)}
    </div>`;
  } else if (bots.length === 0) {
    body = html`<div class="flows-state flows-empty" data-testid="bots-empty">
      <span class="flows-empty-icon" aria-hidden="true"><${Icon} name="IconRobot" size=${32} /></span>
      <h2 class="flows-empty-title">${t('bots.list.emptyTitle')}</h2>
      <p class="flows-empty-text">${t('bots.list.emptyText')}</p>
      <div class="flows-empty-actions">
        <${Button} title=${t('bots.list.startFromTemplate')} icon="IconLayoutGrid" accent="blue" size="small" onClick=${() => navigate(BOT_TEMPLATES_PATH)} />
        <${Button} title=${t('bots.list.createBlank')} variant="secondary" size="small" onClick=${() => setDialog({ kind: 'create' })} />
      </div>
    </div>`;
  } else if (visible.length === 0) {
    body = html`<div class="flows-state" data-testid="bots-no-results">
      <${EmptyState} icon="IconSearch" title=${query.q ? t('bots.list.noResultsFor', { query: query.q }) : t('bots.list.noResults')}
        action=${html`<${Button} title=${t('bots.list.clearFilters')} variant="secondary" size="small" onClick=${() => {
          setSearch('');
          updateSearchParams({ q: null, status: null, purpose: null, channelId: null });
        }} />`} />
    </div>`;
  } else if (isMobile) {
    body = html`<ul class="flows-cards" data-testid="bots-cards">${visible.map((bot) => html`<${BotCard} key=${bot.id} bot=${bot} menuItems=${rowMenuItems(bot)} />`)}</ul>`;
  } else {
    body = html`<${BotsTable} bots=${visible} query=${query} menuItemsOf=${rowMenuItems}
      onSort=${(/** @type {BotSort} */ sort) => setQuery({ sort, order: query.sort === sort ? (query.order === 'asc' ? 'desc' : 'asc') : defaultBotOrderOf(sort) })} />`;
  }

  return html`<${PageCard} className="flows-page bots-page" header=${html`<${PageHeader} icon="IconRobot" title=${t('bots.list.title')} actions=${headerActions} />`}>
    <div class="flows-list" data-testid="bots-page">
      ${hasBots || hasFilters ? html`<${BotListToolbar} query=${query} search=${search} onSearch=${setSearch} channels=${channels} onChange=${setQuery} />` : null}
      ${body}
    </div>
    ${dialog?.kind === 'create'
      ? html`<${BotNameDialog} mode="create" existingNames=${(bots ?? []).map((bot) => bot.name)} onClose=${closeDialog}
          onSubmit=${async (/** @type {{ name: string, purpose: string }} */ value) => {
            try {
              const bot = await createBot({ name: value.name, purpose: value.purpose });
              navigate(botEditorHref(bot.id));
              return true;
            } catch (error) {
              showErrorSnackbar(error);
              return false;
            }
          }} />`
      : null}
    ${dialog?.kind === 'deactivate' || dialog?.kind === 'delete'
      ? html`<${BotLifecycleConfirm} kind=${dialog.kind} name=${dialog.bot.name} isBusy=${isBusy} onClose=${closeDialog} onConfirm=${async () => {
          setIsBusy(true);
          try {
            if (dialog.kind === 'deactivate') {
              const result = await deactivateBot(dialog.bot.id);
              showSuccessSnackbar(t('bots.lifecycle.deactivate.done', { count: result.stoppedRuns }));
            } else {
              await deleteBot(dialog.bot.id);
              showSuccessSnackbar(t('bots.lifecycle.delete.done'));
            }
            closeDialog();
            load();
          } catch (error) {
            showErrorSnackbar(error);
          } finally {
            setIsBusy(false);
          }
        }} />`
      : null}
  <//>`;
}

/**
 * Busca, "Filtros (n)" e os chips dos filtros ligados.
 * @param {{ query: BotListQuery, search: string, onSearch: (value: string) => void, channels: Array<{ id: string, label: string }>,
 *   onChange: (changes: Partial<BotListQuery>) => void }} props
 */
function BotListToolbar({ query, search, onSearch, channels, onChange }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const count = activeBotFilterCount(query);
  /** @param {string} status */
  const toggleStatus = (status) => onChange({ statuses: query.statuses.includes(status) ? query.statuses.filter((value) => value !== status) : [...query.statuses, status] });
  const channelLabel = (/** @type {string} */ id) => channels.find((channel) => channel.id === id)?.label ?? id;
  const chips = [
    ...query.statuses.map((status) => ({ key: `status-${status}`, label: t(`bots.status.${status}`), onRemove: () => toggleStatus(status) })),
    ...(query.purpose ? [{ key: 'purpose', label: t(`bots.purposes.${query.purpose}`), onRemove: () => onChange({ purpose: null }) }] : []),
    ...(query.channelId ? [{ key: 'channel', label: channelLabel(query.channelId), onRemove: () => onChange({ channelId: null }) }] : []),
  ];
  return html`<div class="flows-toolbar">
    <div class="flows-toolbar-row">
      <label class="flows-search">
        <${Icon} name="IconSearch" size=${16} />
        <input type="search" class="flows-search-input" value=${search} placeholder=${t('bots.list.search')} aria-label=${t('bots.list.search')}
          onInput=${(/** @type {InputEvent} */ event) => onSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} data-testid="bots-search" />
      </label>
      <button type="button" ref=${anchorRef} class="flows-filter-button" aria-haspopup="dialog" aria-expanded=${isOpen ? 'true' : 'false'}
        data-active=${count > 0 ? 'true' : undefined} onClick=${() => setIsOpen(!isOpen)} data-testid="bots-filter-button">
        <${Icon} name="IconFilter" size=${16} />
        <span>${count > 0 ? t('bots.list.filtersCount', { count }) : t('bots.list.filters')}</span>
      </button>
      <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-start" width=${260} role="dialog">
        <div class="flows-filter-menu" aria-label=${t('bots.list.filters')}>
          <fieldset class="flows-filter-group">
            <legend>${t('bots.list.filterStatus')}</legend>
            ${BOT_LIST_STATUSES.map((status) => html`<label key=${status} class="flows-filter-option">
              <${Checkbox} checked=${query.statuses.includes(status)} onChange=${() => toggleStatus(status)} ariaLabel=${t(`bots.status.${status}`)} />
              <span>${t(`bots.status.${status}`)}</span>
            </label>`)}
          </fieldset>
          <fieldset class="flows-filter-group">
            <legend>${t('bots.list.filterPurpose')}</legend>
            <select class="text-input" value=${query.purpose ?? ''} aria-label=${t('bots.list.filterPurpose')}
              onChange=${(/** @type {Event} */ event) => onChange({ purpose: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
              <option value="">${t('bots.list.anyPurpose')}</option>
              ${BOT_LIST_PURPOSES.map((purpose) => html`<option key=${purpose} value=${purpose}>${t(`bots.purposes.${purpose}`)}</option>`)}
            </select>
          </fieldset>
          ${channels.length > 0
            ? html`<fieldset class="flows-filter-group">
                <legend>${t('bots.list.filterChannel')}</legend>
                <select class="text-input" value=${query.channelId ?? ''} aria-label=${t('bots.list.filterChannel')}
                  onChange=${(/** @type {Event} */ event) => onChange({ channelId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
                  <option value="">${t('bots.list.anyChannel')}</option>
                  ${channels.map((channel) => html`<option key=${channel.id} value=${channel.id}>${channel.label}</option>`)}
                </select>
              </fieldset>`
            : null}
        </div>
      <//>
    </div>
    ${chips.length > 0
      ? html`<div class="flows-filter-chips" aria-label=${t('bots.list.activeFilters')}>
          ${chips.map((chip) => html`<span key=${chip.key} class="flows-filter-chip">
            <span>${chip.label}</span>
            <button type="button" aria-label=${t('bots.list.removeFilter', { filter: chip.label })} onClick=${chip.onRemove}><${Icon} name="IconX" size=${12} /></button>
          </span>`)}
        </div>`
      : null}
  </div>`;
}

const COLUMNS = /** @type {Array<{ sort: BotSort | null, labelKey: string, numeric?: boolean }>} */ ([
  { sort: 'name', labelKey: 'bots.list.columns.name' },
  { sort: null, labelKey: 'bots.list.columns.purpose' },
  { sort: null, labelKey: 'bots.list.columns.triggers' },
  { sort: 'status', labelKey: 'bots.list.columns.status' },
  { sort: 'started', labelKey: 'bots.list.columns.started', numeric: true },
  { sort: 'handedOff', labelKey: 'bots.list.columns.handedOff', numeric: true },
  { sort: 'completed', labelKey: 'bots.list.columns.completed', numeric: true },
  { sort: 'updatedAt', labelKey: 'bots.list.columns.updatedAt' },
]);

/** Nome do bot com o alerta de pendências. @param {{ bot: BotSummary }} props */
function BotNameCell({ bot }) {
  return html`<span class="flows-name-line">
    <${Link} href=${botEditorHref(bot.id)} className="flows-name-link">${bot.name}<//>
    ${bot.problemCount > 0
      ? html`<span class="flows-problem-icon" title=${t('bots.list.problems', { count: bot.problemCount })} aria-label=${t('bots.list.problems', { count: bot.problemCount })}>
          <${Icon} name="IconAlertTriangle" size=${14} />
        </span>`
      : null}
  </span>`;
}

/** Gatilhos em texto curto. @param {BotSummary} bot */
const triggersText = (bot) => botTriggerKinds(bot).map((kind) => t(`bots.triggers.short.${kind}`)).join(', ') || t('bots.list.noTriggers');

/**
 * @param {{ bots: BotSummary[], query: BotListQuery, onSort: (sort: BotSort) => void, menuItemsOf: (bot: BotSummary) => any[] }} props
 */
function BotsTable({ bots, query, onSort, menuItemsOf }) {
  return html`<div class="flows-table-wrapper">
    <table class="flows-table bots-table" data-testid="bots-table">
      <thead>
        <tr>
          ${COLUMNS.map((column) => {
            if (!column.sort) return html`<th key=${column.labelKey} scope="col">${t(column.labelKey)}</th>`;
            const sort = column.sort;
            const isSorted = query.sort === sort;
            return html`<th key=${sort} scope="col" data-numeric=${column.numeric ? 'true' : undefined}
              aria-sort=${isSorted ? (query.order === 'asc' ? 'ascending' : 'descending') : 'none'}>
              <button type="button" class="flows-sort-button" data-sorted=${isSorted ? 'true' : undefined} onClick=${() => onSort(sort)}>
                <span>${t(column.labelKey)}</span>
                <${Icon} name=${isSorted ? (query.order === 'asc' ? 'IconArrowUp' : 'IconArrowDown') : 'IconArrowsSort'} size=${12} />
              </button>
            </th>`;
          })}
          <th class="flows-table-menu" scope="col"><span class="visually-hidden-text">${t('bots.list.columns.actions')}</span></th>
        </tr>
      </thead>
      <tbody>
        ${bots.map((bot) => html`<tr key=${bot.id} data-testid="bot-row">
          <td><${BotNameCell} bot=${bot} /></td>
          <td><${BotPurposeChip} purpose=${bot.purpose} /></td>
          <td class="bots-triggers-cell">${triggersText(bot)}</td>
          <td><${BotStatusChip} status=${bot.status} inactiveReason=${bot.inactiveReason} /></td>
          <td data-numeric="true">${formatNumber(bot.startedLast30Days)}</td>
          <td data-numeric="true">${botRateText(bot.handedOffRate)}</td>
          <td data-numeric="true">${botRateText(bot.completedRate)}</td>
          <td class="flows-table-date">${lastChangeText(bot.updatedAt)}</td>
          <td class="flows-table-menu">
            <${FlowMenuButton} ariaLabel=${t('bots.list.rowMenu', { name: bot.name })} items=${menuItemsOf(bot)} className="flow-menu-trigger flows-row-menu" />
          </td>
        </tr>`)}
      </tbody>
    </table>
  </div>`;
}

/** Cartão do celular. @param {{ bot: BotSummary, menuItems: any[] }} props */
function BotCard({ bot, menuItems }) {
  return html`<li class="flows-card bots-card" data-testid="bot-card">
    <div class="flows-card-main">
      <${BotNameCell} bot=${bot} />
      <div class="flows-card-meta">
        <${BotStatusChip} status=${bot.status} inactiveReason=${bot.inactiveReason} />
        <${BotPurposeChip} purpose=${bot.purpose} />
      </div>
      <span class="bots-card-line">${triggersText(bot)}</span>
      <span class="bots-card-line">${t('bots.list.cardNumbers', { started: formatNumber(bot.startedLast30Days), handedOff: botRateText(bot.handedOffRate) })}</span>
    </div>
    <${FlowMenuButton} ariaLabel=${t('bots.list.rowMenu', { name: bot.name })} items=${menuItems} className="flow-menu-trigger flows-row-menu" />
  </li>`;
}
